import styled, { css, RuleSet } from 'styled-components'

// Note: no `marginLeft` here — `Badge`'s own `gap` already spaces every flex
// child (icon, label, this button) uniformly. Adding both would double the gap
// in front of the remove button specifically.
export const RemoveButton = styled.div({
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  justifyContent: 'center',
  padding: 0,
})

import { BadgeColorKeys, BadgeSizeKeys, BadgeVariantKeys } from './badge.types'

export const Badge = styled.span<{
  $color: BadgeColorKeys
  $size: BadgeSizeKeys
  $variant: BadgeVariantKeys
}>(({ theme, $color, $size, $variant }) => {
  const baseStyles = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.space['4']};
    box-sizing: border-box;
    font-weight: 500;
    white-space: nowrap;
    border-radius: ${theme.border.radius.full};
  `

  const sizeStyles: Record<BadgeSizeKeys, RuleSet> = {
    'extra-small': css`
      padding: ${theme.space['0']} ${theme.space['6']};
      font-size: 1.2rem;
      line-height: 1.6rem;
    `,
    medium: css`
      padding: ${theme.space['8']} ${theme.space['16']};
      font-size: 1.4rem;
      line-height: 2rem;
    `,
    small: css`
      padding: ${theme.space['4']} ${theme.space['16']};
      font-size: 1.2rem;
      line-height: 1.6rem;
    `,
    tiny: css`
      padding: ${theme.space['2']} ${theme.space['8']};
      font-size: 1.2rem;
      line-height: 1.6rem;
    `,
  }

  const colorMap: Record<BadgeColorKeys, Record<BadgeVariantKeys, RuleSet>> = {
    blue: {
      light: css`
        background-color: ${theme.colors.blue100};
        color: ${theme.colors.blue500};
      `,
      solid: css`
        background-color: ${theme.colors.blue500};
        color: ${theme.colors.white};
      `,
    },
    green: {
      light: css`
        background-color: ${theme.colors.green100};
        color: ${theme.colors.green500};
      `,
      solid: css`
        background-color: ${theme.colors.green500};
        color: ${theme.colors.white};
      `,
    },
    neutral: {
      light: css`
        background-color: ${theme.colors.gray300};
        color: ${theme.colors.black500};
      `,
      solid: css`
        background-color: ${theme.colors.black200};
        color: ${theme.colors.white};
      `,
    },
    orange: {
      light: css`
        background-color: ${theme.colors.orange100};
        color: ${theme.colors.orange500};
      `,
      solid: css`
        background-color: ${theme.colors.orange500};
        color: ${theme.colors.white};
      `,
    },
    purple: {
      light: css`
        background-color: ${theme.colors.purple100};
        color: ${theme.colors.purple500};
      `,
      solid: css`
        background-color: ${theme.colors.purple500};
        color: ${theme.colors.white};
      `,
    },
    red: {
      light: css`
        background-color: ${theme.colors.red100};
        color: ${theme.colors.red500};
      `,
      solid: css`
        background-color: ${theme.colors.red500};
        color: ${theme.colors.white};
      `,
    },
    turquoise: {
      light: css`
        background-color: ${theme.colors.turquoise100};
        color: ${theme.colors.turquoise500};
      `,
      solid: css`
        background-color: ${theme.colors.turquoise500};
        color: ${theme.colors.white};
      `,
    },
  }

  return css`
    ${baseStyles}
    ${sizeStyles[$size]}
    ${colorMap[$color][$variant]}
  `
})
